/**
 * TenantKillSwitchCard — Reusable per-tenant AI kill switch card.
 * Shows AI status (active/halted) with halt/resume controls.
 * Used in AI Policies page and Tenant Admin Dashboard.
 * Phase 4.7 — WQ-178
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

interface TenantKillSwitchCardProps {
  tenantId: string;
  tenantName?: string;
}

interface KillSwitchState {
  active: boolean;
  reason?: string;
  activated_by?: string;
  activated_at?: string;
}

const TenantKillSwitchCard: React.FC<TenantKillSwitchCardProps> = ({ tenantId, tenantName }) => {
  const { authFetch } = useAuth();
  const [killSwitch, setKillSwitch] = useState<KillSwitchState>({ active: false });
  const [showHaltPanel, setShowHaltPanel] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`/api/admin/tenants/${tenantId}/ai/kill-switch`);
      if (res.ok) {
        const data = await res.json();
        setKillSwitch({
          active: data.active === true,
          reason: data.reason,
          activated_by: data.activated_by,
          activated_at: data.activated_at,
        });
      }
    } catch { /* non-critical */ }
  }, [authFetch, tenantId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleActivate = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      await authFetch(`/api/admin/tenants/${tenantId}/ai/kill-switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setReason('');
      setShowHaltPanel(false);
      await fetchStatus();
    } catch { /* handled by re-fetch */ }
    setLoading(false);
  };

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      await authFetch(`/api/admin/tenants/${tenantId}/ai/kill-switch`, { method: 'DELETE' });
      await fetchStatus();
    } catch { /* handled by re-fetch */ }
    setLoading(false);
  };

  if (!killSwitch.active) {
    return (
      <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--text-lg, 18px)', color: 'var(--text-primary)' }}>AI Operations</h3>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              <span style={{ color: '#22c55e', fontWeight: 600 }}>● Active</span> — AI services running normally{tenantName ? ` for ${tenantName}` : ''}
            </div>
          </div>
          <button onClick={() => setShowHaltPanel(!showHaltPanel)} style={{
            fontSize: 12, padding: '6px 14px', borderRadius: 6,
            border: '1px solid rgba(220, 38, 38, 0.3)', background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626', cursor: 'pointer', fontWeight: 600,
          }}>Halt AI for Tenant</button>
        </div>
        {showHaltPanel && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Reason for halting AI (required)"
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8,
                border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }} />
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
              This will halt AI operations for this tenant only. Essential platform agents (OpsAgent, guardrails) will continue running.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleActivate} disabled={loading || !reason.trim()} style={{
                fontSize: 12, padding: '6px 16px', borderRadius: 6, border: 'none',
                background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600,
                opacity: !reason.trim() ? 0.5 : 1,
              }}>Confirm Halt</button>
              <button onClick={() => setShowHaltPanel(false)} style={{
                fontSize: 12, padding: '6px 12px', borderRadius: 6,
                border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--text-lg, 18px)', color: '#dc2626' }}>AI Operations Halted</h3>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            Reason: <strong style={{ color: 'var(--text-primary)' }}>{killSwitch.reason}</strong>
            {killSwitch.activated_by && <> · By: {killSwitch.activated_by}</>}
            {killSwitch.activated_at && <> · {new Date(killSwitch.activated_at).toLocaleString()}</>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            Essential platform agents (monitoring, guardrails) are still running.
          </div>
        </div>
        <button onClick={handleDeactivate} disabled={loading} style={{
          fontSize: 12, padding: '6px 14px', borderRadius: 6, border: 'none',
          background: '#22c55e', color: '#fff', cursor: 'pointer', fontWeight: 600,
        }}>{loading ? 'Resuming...' : 'Resume AI'}</button>
      </div>
    </div>
  );
};

export default TenantKillSwitchCard;
